import React, { useEffect, useState, useMemo, useCallback } from 'react';

import classNames from 'classnames';

import { calculateRowTotals } from '../../utils/calculations';
import { percentFigure } from '../../utils/percentFigure';
import { getIndentClass } from '../../utils/styleUtils';

import RevenueMilestoneFields from './RevenueMilestoneFields';

import { formatFigure } from 'Helpers/formatService';

import CheckCircleIcon from 'Assets/icons/circle-check.svg';
import CircleXMark from 'Assets/icons/circle-xmark.svg';
import EditIcon from 'Assets/icons/edit.svg';

export default function UncommittedRow(props) {
    const { type, label, data, periods, indentLevel, contract, maxRevenueMilestonesCount, revenue, totalUncommittedValue = 0 } = props;

    // Memoize milestones to avoid recalculation
    const milestones = useMemo(() => {
        return data.milestones ? data.milestones : [];
    }, [data.milestones]);

    const [milestonesCount, setMilestonesCount] = useState(milestones.length);
    const [currentMilestones, setCurrentMilestones] = useState(milestones);
    const [totalValue, setTotalValue] = useState(milestones.reduce((sum, milestone) => sum + (milestone.amount || 0), 0));

    // Row-level editing state
    const [isRowEditing, setIsRowEditing] = useState(false);
    const [editedValues, setEditedValues] = useState({});

    // Memoize committed/editable state
    const isCommitted = useMemo(() => !!contract, [contract]);
    const isEditable = useMemo(() => props.canEdit && !isCommitted, [props.canEdit, isCommitted]);

    useEffect(() => {
        // Sort milestones by date to ensure correct chronological order
        const sortedMilestones = [...milestones].sort((a, b) => new Date(a.date) - new Date(b.date));
        setMilestonesCount(sortedMilestones.length);
        setCurrentMilestones(sortedMilestones);
    }, [milestones, periods]);

    // Memoize URL creation
    const saveResourceUrl = useMemo(() => {
        return props.RevenueMilestoneUpdateUrl.replace('REVENUE_ID', revenue.id);
    }, [props.RevenueMilestoneUpdateUrl, revenue.id]);

    // Save resource function for uncommitted milestones
    const saveResource = useCallback(
        (milestonesToSave = null) => {
            if (!isEditable || isCommitted) return;
            const milestones = milestonesToSave || currentMilestones;
            props.setLoading(true);
            axios
                .post(saveResourceUrl, {
                    dates: milestones.map((milestone) => ({
                        date: milestone.date,
                        name: milestone.name,
                        description: milestone.description || null,
                        perc: milestone.percentage,
                    })),
                    section: type === 'cash' ? 'fixed/cash' : 'fixed/vik',
                })
                .then((response) => {
                    props.setLoading(false);
                    props.setRevenueEditing(null, null, null, null);
                    // props.fetchCentralCashflow();
                })
                .catch((e) => {
                    props.setLoading(false);
                    props.modalShow(true, `Error`, `Request Failed`);
                });
        },
        [isEditable, isCommitted, props, saveResourceUrl, currentMilestones, type],
    );

    const saveMilestonePercentage = useCallback(
        (event, i, isDeletion = false) => {
            if (!isEditable || isCommitted) return;
            const currentPeriod = data.currentMilestones[i];
            if (!currentPeriod || !currentPeriod.date) return;
            const milestoneIndex = currentMilestones.findIndex((milestone) => {
                const milestoneDate = new Date(milestone.date);
                const periodDate = new Date(currentPeriod.date);
                return milestoneDate.getFullYear() === periodDate.getFullYear() && milestoneDate.getMonth() === periodDate.getMonth();
            });
            if (event.key === 'Enter') {
                const value = parseFloat(event.target.value);
                // If value is 0 or invalid, treat as deletion
                if (isDeletion || isNaN(value) || value === 0) {
                    if (milestoneIndex !== -1) {
                        const updatedMilestones = currentMilestones.filter((_, index) => index !== milestoneIndex);
                        setCurrentMilestones(updatedMilestones);
                        setMilestonesCount(updatedMilestones.length);
                        saveResource(updatedMilestones);
                    }
                    props.setRevenueEditing(null, null, null, null);
                    return;
                }
                // Validate total percentage
                let totalPercentage = value;
                currentMilestones.forEach((element) => {
                    if (milestoneIndex === -1 || element !== currentMilestones[milestoneIndex]) {
                        totalPercentage += parseInt(element.percentage || 0);
                    }
                });
                if (totalPercentage > 100) {
                    props.modalShow(true, "Percentage can't be more than 100", "The sum of all the percentages of the milestones can't be greater than 100");
                    return;
                }
                let updatedMilestones = [...currentMilestones];
                if (milestoneIndex !== -1) {
                    // Update existing milestone
                    updatedMilestones[milestoneIndex].percentage = value;
                } else {
                    // Add new milestone using the period date
                    const newMilestone = {
                        date: currentPeriod.date,
                        name: `${label} Milestone ${milestonesCount + 1}`,
                        percentage: value,
                        type: type === 'cash' ? 'fixed/cash' : 'fixed/vik',
                    };
                    updatedMilestones.push(newMilestone);
                    setMilestonesCount(milestonesCount + 1);
                }
                // Sort milestones by date
                updatedMilestones = updatedMilestones.sort((a, b) => new Date(a.date) - new Date(b.date));
                setCurrentMilestones(updatedMilestones);
                saveResource(updatedMilestones);
            } else if (event.key === 'Escape') {
                props.setRevenueEditing(null, null, null, null);
            }
        },
        [isEditable, isCommitted, data.currentMilestones, currentMilestones, props, saveResource, type, label, milestonesCount],
    );

    const internalSetEditing = useCallback(
        (milestone_index, editType) => {
            if (!isEditable || isCommitted) return;
            props.setRevenueEditing(revenue.id, milestone_index, editType, type);
        },
        [isEditable, isCommitted, props, revenue.id, type],
    );

    const closeChangeMilestone = useCallback(() => {
        props.setRevenueEditing(null, null, null, null);
    }, [props]);

    // Row-level editing functions
    const handleRowEdit = useCallback(() => {
        if (!isEditable || isCommitted) return;

        // Initialize edited values with current milestone percentages
        const initialValues = {};
        data.currentMilestones?.forEach((milestone, index) => {
            if (milestone?.date) {
                const milestonesInThisPeriod = currentMilestones.filter((m) => {
                    const milestoneDate = new Date(m.date);
                    const periodDate = new Date(milestone.date);
                    return milestoneDate.getFullYear() === periodDate.getFullYear() && milestoneDate.getMonth() === periodDate.getMonth();
                });

                if (milestonesInThisPeriod.length > 0) {
                    initialValues[index] = milestonesInThisPeriod[0].percentage || '';
                } else {
                    initialValues[index] = '';
                }
            }
        });

        setEditedValues(initialValues);
        setIsRowEditing(true);
    }, [isEditable, isCommitted, data.currentMilestones, currentMilestones]);

    const handleRowSave = useCallback(() => {
        if (!isEditable || isCommitted) return;

        // Validate total percentage
        let totalPercentage = 0;
        const validValues = {};

        Object.entries(editedValues).forEach(([index, value]) => {
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue > 0) {
                validValues[index] = numValue;
                totalPercentage += numValue;
            }
        });

        if (totalPercentage > 100) {
            props.modalShow(true, "Percentage can't be more than 100", "The sum of all the percentages of the milestones can't be greater than 100");
            return;
        }

        // Create updated milestones array
        let updatedMilestones = [...currentMilestones];

        // Remove existing milestones for ALL periods being edited (both valid and invalid/empty values)
        Object.keys(editedValues).forEach((index) => {
            const periodIndex = parseInt(index);
            const periodDate = data.currentMilestones[periodIndex]?.date;
            if (periodDate) {
                updatedMilestones = updatedMilestones.filter((milestone) => {
                    const milestoneDate = new Date(milestone.date);
                    const targetDate = new Date(periodDate);
                    return !(milestoneDate.getFullYear() === targetDate.getFullYear() && milestoneDate.getMonth() === targetDate.getMonth());
                });
            }
        });

        // Add new milestones
        var currentMilestoneCount = 1;
        Object.entries(validValues).forEach(([index, value]) => {
            const periodIndex = parseInt(index);
            const periodDate = data.currentMilestones[periodIndex]?.date;
            if (periodDate) {
                const newMilestone = {
                    date: periodDate,
                    name: `${label} Milestone ${currentMilestoneCount++}`,
                    percentage: value,
                    type: type === 'cash' ? 'fixed/cash' : 'fixed/vik',
                };
                updatedMilestones.push(newMilestone);
            }
        });

        // Sort milestones by date
        updatedMilestones = updatedMilestones.sort((a, b) => new Date(a.date) - new Date(b.date));

        setCurrentMilestones(updatedMilestones);
        setMilestonesCount(updatedMilestones.length);
        setIsRowEditing(false);
        setEditedValues({});

        // Save to backend
        saveResource(updatedMilestones);
    }, [isEditable, isCommitted, editedValues, currentMilestones, data.currentMilestones, props, label, milestonesCount, type, saveResource]);

    const handleRowCancel = useCallback(() => {
        setIsRowEditing(false);
        setEditedValues({});
    }, []);

    // Respond to Edit All trigger
    useEffect(() => {
        const t = props.editAllTrigger;
        if (!t?.version) return;

        const matchesTarget = t.target === 'all' || t.target === 'revenues';
        if (!matchesTarget) return;

        setIsRowEditing(!!t.on);
    }, [props.editAllTrigger?.version]);

    const handleValueChange = useCallback((index, value) => {
        setEditedValues((prev) => ({
            ...prev,
            [index]: value,
        }));
    }, []);

    const getTextClass = (level) => {
        if (level > 2 && isCommitted) {
            return '!tw-font-bold';
        }
    };

    const totals = useMemo(() => {
        // While editing, preview totals should reflect in-progress editedValues
        if (isRowEditing && editedValues && data.currentMilestones) {
            const byYearMonth = new Map();

            currentMilestones.forEach((m) => {
                const d = new Date(m.date);
                const key = `${d.getFullYear()}-${d.getMonth()}`;
                byYearMonth.set(key, { ...m });
            });

            Object.entries(editedValues).forEach(([index, value]) => {
                const periodIndex = parseInt(index);
                const periodDate = data.currentMilestones[periodIndex]?.date;
                if (!periodDate) return;

                const d = new Date(periodDate);
                const key = `${d.getFullYear()}-${d.getMonth()}`;
                const num = parseFloat(value);

                if (!isNaN(num) && num > 0) {
                    const existing = byYearMonth.get(key) || { date: periodDate };
                    existing.percentage = num;
                    // Calculate live amount for this milestone
                    existing.amount = totalUncommittedValue > 0 ? (totalUncommittedValue * num) / 100 : 0;
                    byYearMonth.set(key, existing);
                } else if (byYearMonth.has(key)) {
                    const existing = byYearMonth.get(key);
                    existing.percentage = 0;
                    existing.amount = 0;
                    byYearMonth.set(key, existing);
                }
            });

            return calculateRowTotals(Array.from(byYearMonth.values()));
        }

        return calculateRowTotals(currentMilestones);
    }, [currentMilestones, editedValues, isRowEditing, data.currentMilestones, totalUncommittedValue]);

    return (
        <tr className="!tw-bg-white">
            <td className={getIndentClass(indentLevel)}></td>
            <td className="tw-p-4">
                <div className="tw-font-medium">{label}</div>
            </td>
            <td className={classNames('tw-p-4 tw-text-right tw-border-r-2', getTextClass(indentLevel))}>{formatFigure(totalUncommittedValue)}</td>
            {[...Array(maxRevenueMilestonesCount)].map((e, i) => {
                return (
                    <React.Fragment key={`${type}_milestone_${i}`}>
                        <RevenueMilestoneFields
                            key={`${props.keyPrefix || ''}${type}_milestone_${revenue.id}_${i}`}
                            revenue={{
                                ...revenue,
                                currentMilestones: (() => {
                                    const structured = [];
                                    if (data.currentMilestones && data.currentMilestones[i]) {
                                        const periodDate = new Date(data.currentMilestones[i].date);
                                        const milestonesInThisPeriod = currentMilestones.filter((milestone) => {
                                            const milestoneDate = new Date(milestone.date);
                                            return (
                                                milestoneDate.getFullYear() === periodDate.getFullYear() && milestoneDate.getMonth() === periodDate.getMonth()
                                            );
                                        });
                                        if (milestonesInThisPeriod.length > 0) {
                                            const milestone = milestonesInThisPeriod[0];
                                            const amount = totalUncommittedValue > 0 ? (totalUncommittedValue * (milestone.percentage || 0)) / 100 : 0;
                                            structured[i] = {
                                                ...milestone,
                                                amount: amount,
                                            };
                                        }
                                    }
                                    // Always provide the date for empty cells to enable new milestone creation
                                    if (!structured[i] && data.currentMilestones[i]) {
                                        structured[i] = {
                                            date: data.currentMilestones[i].date,
                                        };
                                    }
                                    return structured;
                                })(),
                                id: revenue.id,
                            }}
                            editingRevenueId={props.editingRevenueId}
                            editingType={props.editingType}
                            editingMilestoneId={props.editingMilestoneId}
                            editingMilestoneType={props.editingMilestoneType}
                            internalSetEditing={internalSetEditing}
                            saveMilestonePercentage={saveMilestonePercentage}
                            closeChangeMilestone={closeChangeMilestone}
                            milestoneId={i}
                            milestoneType={type}
                            keyPrefix={props.keyPrefix || ''}
                            canEdit={props.canEdit}
                            // Row-level editing props
                            isRowEditing={isRowEditing}
                            editedValues={editedValues}
                            handleValueChange={handleValueChange}
                            totalUncommittedValue={totalUncommittedValue}
                        />
                    </React.Fragment>
                );
            })}
            <td className="tw-p-4 tw-text-right">{percentFigure(totals.totalPercentage)}</td>
            <td className="tw-p-4 tw-text-right">{formatFigure(totals.totalValue)}</td>
            <td className="tw-p-4 tw-text-center">
                {isEditable && !isCommitted && (
                    <div className="tw-flex tw-justify-center tw-gap-2">
                        {!isRowEditing ? (
                            <button
                                type="button"
                                onClick={handleRowEdit}
                                className="tw-p-2 tw-rounded tw-bg-blue-500 tw-text-white tw-hover:bg-blue-600 tw-transition-colors"
                                title="Edit row"
                            >
                                <EditIcon className="tw-w-4 tw-h-4" />
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={handleRowSave}
                                    className="tw-p-2 tw-rounded tw-bg-green-500 tw-text-white tw-hover:bg-green-600 tw-transition-colors"
                                    title="Save changes"
                                >
                                    <CheckCircleIcon className="tw-w-4 tw-h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={handleRowCancel}
                                    className="tw-p-2 tw-rounded tw-bg-gray-500 tw-text-white tw-hover:bg-gray-600 tw-transition-colors"
                                    title="Cancel changes"
                                >
                                    <CircleXMark className="tw-w-4 tw-h-4" />
                                </button>
                            </>
                        )}
                    </div>
                )}
            </td>
        </tr>
    );
}
